import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  CheckCircle,
  X,
  Database,
  TrendingUp,
  Pause,
  Play,
  RotateCcw,
  AlertTriangle,
  Wifi,
  WifiOff,
  Clock,
} from 'lucide-react';
import { getDuckDBEngine, StreamingProgress as DuckDBProgress, DatasetMetadata } from '../../lib/duckdb-engine';
import { getBigDataBackendService, BackendJob } from '../../lib/big-data-backend';
import { useAuth } from '../auth/AuthProvider';
import StreamingUploader, { UploadProgress as StreamingUploadProgress, UploadPausedError } from '../../lib/streaming-upload';
import { getStreamingUploader } from '../../lib/streaming-upload';
import { useUploadStore, type UploadSessionState } from '../../stores/upload-store';
import { useUploadSession } from '../../hooks/useUploadSession';
import { debugLog } from '../../utils/debug-logger';
import logger from '../../utils/logger';

// ───────────── Types ─────────────

interface BrowserUploadProgress {
  bytesProcessed: number;
  totalBytes: number;
  rowsProcessed: number;
  estimatedRows: number;
  percentage: number;
  stage: 'uploading' | 'parsing' | 'indexing' | 'analyzing' | 'complete';
}

interface BigDataUploaderProps {
  onUploadComplete: (metadata: DatasetMetadata) => void;
  onError: (error: string) => void;
}

// ───────────── Helpers ─────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatETA(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '--';
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.ceil(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function formatSpeed(bytesPerSec: number): string {
  if (!bytesPerSec || bytesPerSec <= 0) return '-- MB/s';
  const mbps = bytesPerSec / (1024 * 1024);
  return mbps >= 1 ? `${mbps.toFixed(1)} MB/s` : `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
}

// ───────────── Component ─────────────

const BigDataUploader: React.FC<BigDataUploaderProps> = ({ onUploadComplete, onError }) => {
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<BrowserUploadProgress | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [useBackend, setUseBackend] = useState(false);
  const [backendProgress, setBackendProgress] = useState<{
    upload: number;
    processing?: BackendJob;
  } | null>(null);
  const [streamingProgress, setStreamingProgress] = useState<StreamingUploadProgress | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploaderRef = useRef<StreamingUploader | null>(null);

  const duckdb = getDuckDBEngine();
  const backendService = getBigDataBackendService();

  // Zustand store
  const { pendingSessions, loadPendingSessions, removeUpload } = useUploadStore();

  // Realtime hook for live progress
  const { activeUploads: realtimeUploads } = useUploadSession({ userId: user?.id });

  // Load pending sessions on mount
  useEffect(() => {
    if (user?.id) {
      loadPendingSessions(user.id);
    }
  }, [user?.id, loadPendingSessions]);

  // ───── Validate ─────

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const validExtensions = ['.csv', '.json', '.txt', '.xlsx', '.xls', '.parquet'];
    const validTypes = [
      'text/csv', 'application/json', 'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/octet-stream',
    ];
    const hasValidType = validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

    if (!hasValidType && !hasValidExtension) {
      return { valid: false, error: 'Format non supporté. Utilisez CSV, JSON, Excel (.xlsx/.xls) ou Parquet.' };
    }
    if (file.size > 10 * 1024 * 1024 * 1024) {
      return { valid: false, error: 'Fichier trop volumineux (max 10GB).' };
    }
    if (file.size < 1024) {
      return { valid: false, error: 'Fichier trop petit. Minimum 1KB.' };
    }
    return { valid: true };
  };

  // ───── File selection ─────

  const handleFileSelect = useCallback(
    (file: File) => {
      const validation = validateFile(file);
      if (!validation.valid) {
        onError(validation.error!);
        return;
      }
      setSelectedFile(file);
    },
    [onError]
  );

  // ───── Drag & drop ─────

  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileSelect(files[0]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) handleFileSelect(files[0]);
  };

  // ───── Start new upload ─────

  const startUpload = async () => {
    if (!selectedFile || !user) return;
    setUseBackend(true);
    setIsUploading(true);
    setBackendProgress({ upload: 0 });
    setStreamingProgress(null);
    setIsPaused(false);

    try {
      const uploader = getStreamingUploader();
      uploaderRef.current = uploader;

      const { uploadId, s3Key } = await uploader.upload(selectedFile, user.id, {
        onProgress: (prog: StreamingUploadProgress) => {
          setStreamingProgress(prog);
          setBackendProgress((prev) => ({ ...prev!, upload: prog.percentage }));
        },
        onChunkComplete: () => {},
        onPaused: () => {
          setIsPaused(true);
          setIsUploading(false);
        },
        onError: (err) => {
          logger.error('[BigDataUploader] Chunk error:', err);
        },
      });

      debugLog('[BigDataUploader] Upload complete, waiting for processing...');

      // Wait for backend processing
      const job = await backendService.waitForCompletion(uploadId, (processingJob) => {
        setBackendProgress((prev) => ({ ...prev!, processing: processingJob }));
      });

      const metadata: DatasetMetadata = {
        id: job.id,
        name: job.fileName,
        rowCount: job.metadata!.rowCount,
        columnCount: job.metadata!.columnCount,
        columns: job.metadata!.columns.map((col) => ({
          name: col.name, type: col.type, nullable: true, unique: 0, nullCount: 0,
        })),
        size: job.fileSize,
        uploadedAt: new Date(job.startedAt),
        // @ts-ignore
        isBackend: true,
        backendJobId: job.id,
      };

      setIsUploading(false);
      setBackendProgress(null);
      setStreamingProgress(null);
      onUploadComplete(metadata);
    } catch (error: any) {
      if (error instanceof UploadPausedError) {
        // Upload was paused — don't treat as error
        setIsPaused(true);
        setIsUploading(false);
        return;
      }
      logger.error('[BigDataUploader] Upload error:', error);
      setIsUploading(false);
      setBackendProgress(null);
      setStreamingProgress(null);
      onError(`Erreur: ${error.message || error}`);
    }
  };

  // ───── Resume existing upload ─────

  const resumeUpload = async (session: UploadSessionState) => {
    if (!user) return;

    // User must re-select the same file
    if (!selectedFile || selectedFile.name !== session.fileName) {
      onError(`Pour reprendre, sélectionnez le même fichier: "${session.fileName}"`);
      return;
    }

    setIsResuming(true);
    setIsUploading(true);
    setUseBackend(true);
    setIsPaused(false);
    setBackendProgress({ upload: Math.round((session.bytesUploaded / session.fileSize) * 100) });

    try {
      const uploader = getStreamingUploader();
      uploaderRef.current = uploader;

      const { uploadId, s3Key } = await uploader.resume(session, selectedFile, user.id, {
        onProgress: (prog: StreamingUploadProgress) => {
          setStreamingProgress(prog);
          setBackendProgress((prev) => ({ ...prev!, upload: prog.percentage }));
        },
        onPaused: () => {
          setIsPaused(true);
          setIsUploading(false);
        },
        onError: (err) => {
          logger.error('[BigDataUploader] Resume chunk error:', err);
        },
      });

      // Wait for backend processing
      const job = await backendService.waitForCompletion(uploadId, (processingJob) => {
        setBackendProgress((prev) => ({ ...prev!, processing: processingJob }));
      });

      const metadata: DatasetMetadata = {
        id: job.id,
        name: job.fileName,
        rowCount: job.metadata!.rowCount,
        columnCount: job.metadata!.columnCount,
        columns: job.metadata!.columns.map((col) => ({
          name: col.name, type: col.type, nullable: true, unique: 0, nullCount: 0,
        })),
        size: job.fileSize,
        uploadedAt: new Date(job.startedAt),
        // @ts-ignore
        isBackend: true,
        backendJobId: job.id,
      };

      setIsUploading(false);
      setIsResuming(false);
      setBackendProgress(null);
      setStreamingProgress(null);
      removeUpload(session.uploadId);
      onUploadComplete(metadata);
    } catch (error: any) {
      if (error instanceof UploadPausedError) {
        setIsPaused(true);
        setIsUploading(false);
        setIsResuming(false);
        return;
      }
      logger.error('[BigDataUploader] Resume error:', error);
      setIsUploading(false);
      setIsResuming(false);
      setBackendProgress(null);
      setStreamingProgress(null);
      onError(`Erreur reprise: ${error.message || error}`);
    }
  };

  // ───── Pause / Unpause ─────

  const handlePause = () => {
    uploaderRef.current?.pause();
    setIsPaused(true);
  };

  const handleUnpause = () => {
    uploaderRef.current?.unpause();
    setIsPaused(false);
  };

  // ───── Cancel ─────

  const cancelUpload = () => {
    uploaderRef.current?.cancel();
    setSelectedFile(null);
    setIsUploading(false);
    setProgress(null);
    setStreamingProgress(null);
    setBackendProgress(null);
    setIsPaused(false);
  };

  // ───── Browser upload (small files) ─────

  const startBrowserUpload = async () => {
    if (!selectedFile || !user) return;
    setIsUploading(true);
    setProgress({ bytesProcessed: 0, totalBytes: selectedFile.size, rowsProcessed: 0, estimatedRows: 0, percentage: 0, stage: 'uploading' });

    try {
      await duckdb.initialize();
      const metadata = await duckdb.loadCSVFile(selectedFile, (prog: DuckDBProgress) => {
        setProgress({
          bytesProcessed: prog.bytesProcessed, totalBytes: prog.totalBytes,
          rowsProcessed: prog.rowsProcessed, estimatedRows: prog.estimatedRows,
          percentage: prog.percentage, stage: prog.stage as BrowserUploadProgress['stage'],
        });
      });
      setIsUploading(false);
      setProgress(null);
      onUploadComplete(metadata);
    } catch (error) {
      setIsUploading(false);
      setProgress(null);
      onError(`Erreur: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ===== RENDER =====

  return (
    <div className="space-y-6">
      {/* ═══════ Pending sessions banner ═══════ */}
      {pendingSessions.length > 0 && !isUploading && (
        <div className="space-y-3">
          {pendingSessions.map((session) => {
            const pct = session.totalChunks > 0
              ? Math.round((session.uploadedChunks / session.totalChunks) * 100)
              : 0;

            return (
              <div
                key={session.uploadId}
                className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      {session.uploadStatus === 'paused' ? (
                        <Pause className="w-5 h-5 text-amber-500" />
                      ) : (
                        <WifiOff className="w-5 h-5 text-amber-500" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-theme-primary">
                        Upload interrompu — {session.fileName}
                      </h4>
                      <p className="text-sm text-theme-secondary">
                        {session.uploadedChunks}/{session.totalChunks} chunks ({pct}%)
                        — {formatFileSize(session.bytesUploaded)} / {formatFileSize(session.fileSize)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeUpload(session.uploadId)}
                    className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Supprimer cette session"
                  >
                    <X className="w-4 h-4 text-red-400" />
                  </button>
                </div>

                {/* Progress bar */}
                <div className="relative h-2 bg-amber-500/10 rounded-full overflow-hidden mb-3">
                  <div
                    className="absolute inset-y-0 left-0 bg-amber-500 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5 flex-1">
                    <RotateCcw className="w-4 h-4" />
                    Sélectionnez le fichier <strong>"{session.fileName}"</strong> pour reprendre
                  </p>
                  {selectedFile?.name === session.fileName && (
                    <button
                      onClick={() => resumeUpload(session)}
                      className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      Reprendre l'upload
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══════ Upload zone ═══════ */}
      {!selectedFile && !isUploading && (
        <div
          className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
            isDragging
              ? 'border-accent-tertiary bg-accent-tertiary/10 scale-102'
              : 'border-theme hover:border-theme'
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.txt,.xlsx,.xls,.parquet"
            onChange={handleFileInputChange}
            className="hidden"
          />

          <div className="flex flex-col items-center space-y-4">
            <div className="w-20 h-20 bg-gradient-to-r from-orange-600 to-purple-600 rounded-full flex items-center justify-center">
              <Upload className="w-10 h-10 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-theme-primary mb-2">
                Glissez votre fichier ici
              </h3>
              <p className="text-theme-secondary">ou cliquez pour sélectionner</p>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-3 bg-gradient-to-r from-orange-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Sélectionner un fichier
            </button>
            <div className="text-sm text-theme-secondary space-y-1">
              <p>CSV, JSON supportés — jusqu'à 10GB</p>
              <p className="flex items-center gap-1.5 justify-center text-green-600 dark:text-green-400">
                <Wifi className="w-3.5 h-3.5" />
                Upload résistant aux déconnexions — reprise automatique
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ File selected — Preview ═══════ */}
      {selectedFile && !isUploading && !isPaused && (
        <div className="bg-theme-surface/50 backdrop-blur-sm border border-theme rounded-2xl p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-theme-primary mb-1">{selectedFile.name}</h4>
                <p className="text-sm text-theme-secondary">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <button onClick={() => setSelectedFile(null)} className="p-2 hover:bg-red-500/20 rounded-lg transition-colors">
              <X className="w-5 h-5 text-red-400" />
            </button>
          </div>

          <div className="mt-6 flex space-x-3">
            <button
              onClick={startUpload}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Commencer l'analyse
            </button>
            <button
              onClick={() => setSelectedFile(null)}
              className="px-6 py-3 bg-theme-surface border border-theme text-theme-primary rounded-xl font-semibold hover:bg-theme-surface/80 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ═══════ Upload Progress — Streaming (with pause/resume) ═══════ */}
      {(isUploading || isPaused) && useBackend && (
        <div className="bg-theme-surface/50 backdrop-blur-sm border border-theme rounded-2xl p-6">
          <div className="space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isPaused ? 'bg-amber-500/20' : backendProgress?.processing ? 'bg-blue-500/20' : 'bg-purple-500/20 animate-pulse'
                }`}>
                  {isPaused ? (
                    <Pause className="w-5 h-5 text-amber-500" />
                  ) : backendProgress?.processing ? (
                    <Database className="w-5 h-5 text-blue-400" />
                  ) : (
                    <Upload className="w-5 h-5 text-purple-400" />
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-theme-primary">
                    {isPaused
                      ? 'Upload en pause'
                      : backendProgress?.processing
                      ? `Traitement — ${backendProgress.processing.status}`
                      : isResuming
                      ? 'Reprise de l\'upload...'
                      : 'Envoi en cours'}
                  </h4>
                  <p className="text-sm text-theme-secondary">
                    {streamingProgress
                      ? `${streamingProgress.uploadedChunks}/${streamingProgress.totalChunks} chunks — ${streamingProgress.percentage}%`
                      : backendProgress?.processing
                      ? `${backendProgress.processing.progress.toFixed(0)}% terminé`
                      : `${backendProgress?.upload.toFixed(0)}% envoyé`}
                  </p>
                </div>
              </div>

              {/* Pause / Resume / Cancel buttons */}
              <div className="flex items-center gap-2">
                {!backendProgress?.processing && (
                  <>
                    {isPaused ? (
                      <button
                        onClick={handleUnpause}
                        className="p-2.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors"
                        title="Reprendre"
                      >
                        <Play className="w-5 h-5 text-green-500" />
                      </button>
                    ) : (
                      <button
                        onClick={handlePause}
                        className="p-2.5 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg transition-colors"
                        title="Mettre en pause"
                      >
                        <Pause className="w-5 h-5 text-amber-500" />
                      </button>
                    )}
                  </>
                )}
                <button
                  onClick={cancelUpload}
                  className="p-2.5 hover:bg-red-500/20 rounded-lg transition-colors"
                  title="Annuler"
                >
                  <X className="w-5 h-5 text-red-400" />
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="relative h-3 bg-neutral-700/30 rounded-full overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out ${
                  isPaused
                    ? 'bg-amber-500'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600'
                }`}
                style={{
                  width: `${backendProgress?.processing
                    ? backendProgress.processing.progress
                    : backendProgress?.upload || 0}%`,
                }}
              >
                {!isPaused && <div className="absolute inset-0 bg-white/20 animate-pulse" />}
              </div>
            </div>

            {/* Stats grid */}
            {streamingProgress && (
              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-theme">
                <div className="text-center">
                  <p className="text-2xl font-bold text-accent-tertiary">
                    {streamingProgress.percentage}%
                  </p>
                  <p className="text-xs text-theme-secondary mt-1">Progression</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-400">
                    {formatSpeed(streamingProgress.uploadSpeed)}
                  </p>
                  <p className="text-xs text-theme-secondary mt-1">Vitesse</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {formatFileSize(streamingProgress.bytesUploaded)}
                  </p>
                  <p className="text-xs text-theme-secondary mt-1">
                    / {formatFileSize(streamingProgress.totalBytes)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-400 flex items-center justify-center gap-1">
                    <Clock className="w-5 h-5" />
                    {formatETA(streamingProgress.estimatedTimeRemaining)}
                  </p>
                  <p className="text-xs text-theme-secondary mt-1">Temps restant</p>
                </div>
              </div>
            )}

            {/* Backend processing pipeline */}
            {backendProgress?.processing && (
              <div className="space-y-4 pt-4 border-t border-theme">
                <div className="grid grid-cols-6 gap-2">
                  {[
                    { key: 'downloading', label: 'Download', icon: '📥', progress: 10 },
                    { key: 'parsing', label: 'Parse', icon: '📊', progress: 30 },
                    { key: 'analyzing', label: 'Analyze', icon: '🔍', progress: 60 },
                    { key: 'indexing', label: 'Index', icon: '⚡', progress: 70 },
                    { key: 'optimizing', label: 'Optimize', icon: '📦', progress: 85 },
                    { key: 'completed', label: 'Done', icon: '✅', progress: 100 },
                  ].map((step) => {
                    const isActive = backendProgress.processing?.status === step.key;
                    const isDone = backendProgress.processing && backendProgress.processing.progress > step.progress;
                    return (
                      <div
                        key={step.key}
                        className={`text-center p-2 rounded-lg transition-all ${
                          isActive ? 'bg-blue-500/20 border-2 border-blue-500 scale-105'
                            : isDone ? 'bg-green-500/10 border border-green-500/50'
                            : 'bg-neutral-800/30 border border-neutral-700'
                        }`}
                      >
                        <div className="text-2xl mb-1">{step.icon}</div>
                        <div className={`text-xs font-medium ${
                          isActive ? 'text-blue-400' : isDone ? 'text-green-400' : 'text-neutral-500'
                        }`}>
                          {step.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pause info */}
            {isPaused && (
              <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg p-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Upload en pause — votre progression est sauvegardée. Vous pouvez quitter et reprendre plus tard.
              </div>
            )}

            {/* Resilience info */}
            {!isPaused && !backendProgress?.processing && (
              <div className="text-sm text-blue-600 dark:text-blue-400 bg-blue-500/10 rounded-lg p-3 flex items-center gap-2">
                <Wifi className="w-4 h-4 flex-shrink-0" />
                Upload résistant aux coupures — chaque chunk est sauvegardé individuellement. Reprise automatique en cas de déconnexion.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════ Browser progress (small files, legacy) ═══════ */}
      {isUploading && !useBackend && progress && (
        <div className="bg-theme-surface/50 backdrop-blur-sm border border-theme rounded-2xl p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-theme-primary">Traitement navigateur — {progress.percentage.toFixed(0)}%</h4>
              <p className="text-sm text-theme-secondary">
                {formatFileSize(progress.bytesProcessed)} / {formatFileSize(progress.totalBytes)}
              </p>
            </div>
            <div className="relative h-3 bg-neutral-700/30 rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-600 to-purple-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress.percentage}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ Info cards ═══════ */}
      {!selectedFile && !isUploading && pendingSessions.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-theme-surface/30 border border-theme rounded-xl p-4">
            <h4 className="font-semibold text-theme-primary mb-2">Ultra Rapide</h4>
            <p className="text-sm text-theme-secondary">Traitement optimisé jusqu'à 10GB</p>
          </div>
          <div className="bg-theme-surface/30 border border-theme rounded-xl p-4">
            <h4 className="font-semibold text-theme-primary mb-2">Reprise automatique</h4>
            <p className="text-sm text-theme-secondary">Déconnexion ? L'upload reprend où il s'est arrêté</p>
          </div>
          <div className="bg-theme-surface/30 border border-theme rounded-xl p-4">
            <h4 className="font-semibold text-theme-primary mb-2">IA Intégrée</h4>
            <p className="text-sm text-theme-secondary">Insights automatiques avec questions naturelles</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BigDataUploader;
